import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'
import { makeAutoObservable, reaction } from 'mobx'
import { SelectOptionItem } from 'reusable/mui/select-with-label/SelectWithLabel'
import OrgGuardStore from '../OrgGuardStore'
import ModalState from 'reusable/mobx-helpers/ModalState'
import { notNull } from '@gap/utils/utils'
import WebSocketStore from 'websockets/WebSocketStore'
import useWindowSize from 'reusable/hooks/useWindowSize'
import RouterStore from '../RouterStore'
import React from 'react'
import { Theme } from '@mui/material/styles'
import { OrgSubscriptionTierStore } from './org-subscription-tiers/OrgSubscriptionTierStore'
import { CacheHandler } from 'reusable/utils/cache'
import BannerTrialPeriod from './centred-banner/instances/BannerTrialPeriod'
import { UserOrgWithOrgDto } from '@gap/backend/app-services/user-org/models/UserOrgWithOrgDto'

interface MainContainerStoreParams {
   orgGuardStore: OrgGuardStore
   webSocketStore: WebSocketStore
   routerStore: RouterStore
   orgSubscriptionTierStore: OrgSubscriptionTierStore
   windowSize: ReturnType<typeof useWindowSize>
   theme: Theme,
}

export class MainContainerStore {
   sp
   /** defines if the menu drawer is open */
   menuState = new ModalState({ initial: true })
   notificationsState = new ModalState()
   menuComponentModal = {
      title: '',
      state: new ModalState(),
      component: null as null | React.FunctionComponent
   }
   org: UserOrgWithOrgDto | undefined
   isViewingDocWorkspace: boolean = false
   hasHandledExpiredTrial = false
   /** Controls whether the main content should slide out for fullscreen transitions */
   fullscreenSlideOpen = false
   /** When true, skip slide animations (used for instant navigation like "Add Template") */
   skipTransition = false

   constructor(sp: MainContainerStoreParams) {
      makeAutoObservable(this)
      this.sp = sp
      this.org = sp.orgGuardStore.org
      // Menu is always closed by default unless we are desktop
      if (sp.windowSize.screenLayout !== 'desktop') {
         this.menuState.close()
      }

      reaction(
         () => ({
            userOrg: this.sp.orgGuardStore.userData.userOrg,
            pathWithoutRegion: this.sp.orgGuardStore.sp.regionRouterStore.pathWithoutRegion
         }),
         ({ userOrg, pathWithoutRegion }) => {
            const orgDefaultPattern = /^\/org\/[0-7][0-9A-HJKMNP-TV-Z]{25}$/
            // If org data is loaded and current path is fully resolved
            if (!this.hasHandledExpiredTrial && !!userOrg && !pathWithoutRegion.match(orgDefaultPattern)) {
               this.hasHandledExpiredTrial = true
               this.sp.orgSubscriptionTierStore.handleTrialIfExpired()
            }
         }
      );
   }

   get isAdvisorOrg() {
      return this.sp.orgGuardStore.userData.userOrg.orgType === 'advisor'
   }

   get orgList(): SelectOptionItem[] {
      return this.sp.orgGuardStore.userData.orgs?.map(org => ({
         id: org.orgId,
         label: org.name,
      })) || []
   }

   get centredBanners() {
      return [
         ...(this.sp.orgGuardStore.userOrg.tierName === 'Trial' ? [
            <BannerTrialPeriod key="trial-period" />
         ] : []),
      ];
   }

   navigateToOrgsDefault = () => {
      if (!this.sp.orgSubscriptionTierStore.handleTrialIfExpired()) {
         this.sp.routerStore.navigateToOrgsDefault()
      }
   }

   openFullscreenSlide = () => {
      this.fullscreenSlideOpen = true
   }

   closeFullscreenSlide = () => {
      this.fullscreenSlideOpen = false
   }

   setSkipTransition = (value: boolean) => {
      this.skipTransition = value
   }

   subscribeToMigrationJobCompletion = async () => {
      if (!this.sp.orgGuardStore.userData) {
         return
      }
      const user = notNull(this.sp.orgGuardStore.userData)
      await this.sp.webSocketStore.addSubscriptions(
         {
            orgId: notNull(user.orgs)[0].orgId,
            userId: user.id,
            subscriptions: [
               {
                  event: 'migrationjob-completed',
                  entityId: 'migrationjob-completed',
                  callback: async response => {
                     // On migration, we clear the cache and reload the screen
                     sessionStorage.clear()
                     CacheHandler.clearAll()
                     window.location.reload()
                  }
               }
            ]
         }
      )
   }
}

export const { Provider: MainContainerStoreProvider, useProvidedStore: useMainContainerStore } =
   createStoreContext(MainContainerStore)
